"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import type { RootState } from "@/store/store";

/**
 * Skill Cards (section 3006 · variant 3000) — PRD 2015:3259.
 * Heading/sub-heading over a 2-per-row grid of containers (image + title,
 * subtitle, divider, 3-format paragraph), View More/Less reveal (4 visible on
 * desktop, 3 on mobile), optional closing CTA with system behaviour.
 *
 * RESPONSIVE VIA CONTAINER QUERIES, not media queries: the section root is a
 * `@container` and every breakpoint is `@min-[…]`/`@max-[…]` on its own width.
 * On the live page the section spans the viewport, so 1080/1230/1430/1750
 * behave exactly like the design's media breakpoints — but in the admin
 * preview (a ~800px pane while editing) the widget correctly falls back to the
 * narrower layouts instead of cramming desktop sizes into a half-width canvas.
 */

const DESKTOP_VISIBLE = 4;
const MOBILE_VISIBLE = 3;
const MAX_BULLETS = 5;
const MAX_ICON_LINES = 4;

type Row = Record<string, any>;

const isOn = (v: unknown) => v === true || v === 1 || v === "1";

/** bullets / icon-lines are stored one per line in meta_paragraph. */
const splitLines = (raw: unknown): string[] =>
  String(raw ?? "")
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

const ExportIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M13 11 21.2 2.8" />
    <path d="M21.5 6.8V2.5h-4.3" />
    <path d="M11 2.5H9c-5 0-7 2-7 7v6c0 5 2 7 7 7h6c5 0 7-2 7-7v-2" />
  </svg>
);

/** One container card. Hover config is desktop-only (PRD) — state is attached
 *  only when the viewport is resolved non-mobile, so touch devices never see it. */
const SkillCard = ({ row, hoverEnabled }: { row: Row; hoverEnabled: boolean }) => {
  const [hovered, setHovered] = useState(false);

  const title = String(row.meta_title ?? "").trim();
  const subtitle = String(row.meta_subtitle ?? "").trim();
  const format = String(row.meta_paragraph_format ?? "bullets");
  const lines = splitLines(row.meta_paragraph);
  const paragraphText = String(row.meta_paragraph ?? "").trim();
  const hasParagraph = format === "paragraph" ? Boolean(paragraphText) : lines.length > 0;

  const image =
    hovered && row.meta_hover_image ? row.meta_hover_image : row.meta_image;
  // Mobile card creative (stacked layout). Mandatory in the admin form; the
  // fallback to the main image only covers rows saved before the field
  // existed. No hover swap — hover is desktop-only per PRD.
  const imageMobile = row.meta_image_mobile || row.meta_image;
  const icon = hovered && row.meta_hover_icon ? row.meta_hover_icon : row.meta_icon;
  const bg =
    hovered && row.meta_hover_bg_color
      ? row.meta_hover_bg_color
      : row.meta_bg_color || "#FFF3F6";
  const hoverText = hovered ? String(row.meta_hover_text_color ?? "").trim() : "";
  const titleColor = hoverText || "#9F1C34";
  const subtitleColor = hoverText || "rgba(0,0,0,0.8)";
  const paragraphColor = hoverText || row.meta_paragraph_color || "#000000";

  const body = (
    <div
      className={cn(
        "flex size-full flex-col items-center justify-center overflow-clip rounded-[16px] transition-colors",
        "@min-[1080px]:flex-row @min-[1080px]:rounded-[24px]",
        // Permanent transparent border so the hover border doesn't shift layout;
        // on hover it takes the configured hover BG colour (desktop only).
        "border border-transparent",
      )}
      style={{
        backgroundColor: bg,
        borderColor:
          hovered && row.meta_hover_bg_color
            ? row.meta_hover_bg_color
            : "transparent",
      }}
      onMouseEnter={hoverEnabled ? () => setHovered(true) : undefined}
      onMouseLeave={hoverEnabled ? () => setHovered(false) : undefined}
    >
      {/* Image: full-width on top (narrow) / fixed-size left column (wide).
          Shadow: toggle + colour only — geometry fixed per design. */}
      <div
        className={cn(
          "relative h-[170px] w-full shrink-0 rounded-[16px] @min-[1080px]:rounded-[24px]",
          // Wide view: the design heights are MINIMUMS and the column
          // self-stretches, so when long content makes the card taller the
          // image (fill + object-cover) grows to cover the full card height
          // instead of floating at its fixed size.
          "@min-[1080px]:h-auto @min-[1080px]:self-stretch",
          "@min-[1080px]:min-h-[247px] @min-[1080px]:w-[170px]",
          "@min-[1230px]:min-h-[292px] @min-[1230px]:w-[200px]",
          "@min-[1430px]:min-h-[350px] @min-[1430px]:w-[240px]",
          "@min-[1750px]:min-h-[380px] @min-[1750px]:w-[261px]",
          isOn(row.meta_image_shadow) &&
            "@max-[1080px]:shadow-[0px_2px_14px_0px_var(--sc-shadow)] @min-[1080px]:shadow-[4px_0px_16px_0px_var(--sc-shadow)]",
        )}
        style={
          {
            "--sc-shadow": row.meta_image_shadow_color || "rgba(0,0,0,0.25)",
          } as React.CSSProperties
        }
      >
        {/* Container queries can't drive a <picture> media attribute, so the
            two sources are separate images gated by container width. The
            hidden one is display:none + lazy, so it never intersects and the
            browser skips its download. */}
        {imageMobile && (
          <Image
            src={imageMobile}
            alt={title || subtitle || ""}
            fill
            sizes="100vw"
            className="rounded-[16px] object-cover @min-[1080px]:hidden"
          />
        )}
        {image && (
          <Image
            src={image}
            alt={title || subtitle || ""}
            fill
            sizes="261px"
            className="hidden rounded-[24px] object-cover @min-[1080px]:block"
          />
        )}
      </div>

      {/* Text column — stretches to the image height in the row layout and
          starts its content from the top (Figma: self-stretch, items-start),
          instead of floating vertically centred beside the image. */}
      <div
        className={cn(
          "flex w-full min-w-0 flex-1 flex-col gap-[16px] px-[16px] py-[10px]",
          "@min-[1080px]:self-stretch",
          "@min-[1080px]:gap-[10px] @min-[1080px]:p-[10px]",
          "@min-[1230px]:gap-[12px] @min-[1230px]:p-[16px]",
          "@min-[1430px]:gap-[16px] @min-[1430px]:p-[20px]",
        )}
      >
        {(title || subtitle) && (
          <div className="flex flex-col gap-[8px] [word-break:break-word] @min-[1080px]:gap-[4px] @min-[1230px]:gap-[8px]">
            {title && (
              <p
                className={cn(
                  "font-poppins font-semibold leading-[1.4]",
                  "text-[16px] tracking-[-0.08px]",
                  "@min-[1080px]:text-[18px] @min-[1080px]:tracking-[-0.09px]",
                  "@min-[1230px]:text-[20px] @min-[1430px]:text-[22px] @min-[1750px]:text-[24px]",
                )}
                style={{ color: titleColor }}
              >
                {title}
              </p>
            )}
            {subtitle && (
              <p
                className={cn(
                  "font-poppins font-normal leading-[1.55]",
                  "text-[13px] @min-[1230px]:text-[14px] @min-[1430px]:text-[15px] @min-[1750px]:text-[16px]",
                )}
                style={{ color: subtitleColor }}
              >
                {subtitle}
              </p>
            )}
          </div>
        )}

        {isOn(row.meta_divider) && (
          <div
            className="h-px w-full shrink-0 transition-colors"
            style={{
              backgroundColor:
                (hovered && row.meta_hover_divider_color) ||
                row.meta_divider_color ||
                "rgba(0,0,0,0.12)",
            }}
          />
        )}

        {hasParagraph && format === "bullets" && (
          <ul
            className={cn(
              "list-disc ps-[19.5px] font-poppins font-normal leading-[1.55] [word-break:break-word]",
              "text-[13px] @min-[1230px]:text-[14px] @min-[1430px]:text-[15px] @min-[1750px]:text-[16px]",
            )}
            style={{ color: paragraphColor }}
          >
            {lines.slice(0, MAX_BULLETS).map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        )}

        {hasParagraph && format === "paragraph" && (
          <p
            className={cn(
              "line-clamp-6 font-poppins font-normal leading-[1.55] [word-break:break-word]",
              "text-[13px] @min-[1230px]:text-[14px] @min-[1430px]:text-[15px] @min-[1750px]:text-[16px]",
            )}
            style={{ color: paragraphColor }}
          >
            {paragraphText}
          </p>
        )}

        {hasParagraph && format === "icon_lines" && (
          <div className="flex flex-col gap-[8px]">
            {lines.slice(0, MAX_ICON_LINES).map((l, i) => (
              // Text classes live on the ROW so the icon wrapper's h-[1lh]
              // equals exactly one line of THIS text at every breakpoint: the
              // icon centres on the first line and stays there when the text
              // wraps (items-start keeps it pinned to the top line).
              <div
                key={i}
                className={cn(
                  "flex items-start gap-[8px] font-poppins font-normal leading-[1.55]",
                  "text-[13px] @min-[1230px]:text-[14px] @min-[1430px]:text-[15px] @min-[1750px]:text-[16px]",
                )}
              >
                {icon && (
                  <span className="flex h-[1lh] shrink-0 items-center">
                    <span className="relative size-[18px]">
                      <Image src={icon} alt="" fill sizes="18px" className="object-contain" />
                    </span>
                  </span>
                )}
                <p
                  className="[word-break:break-word]"
                  style={{ color: paragraphColor }}
                >
                  {l}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const link = String(row.meta_link ?? "").trim();
  if (!link) return body;
  if (/^https?:\/\//i.test(link)) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer" className="block size-full">
        {body}
      </a>
    );
  }
  return (
    <Link href={link.startsWith("/") ? link : `/${link}`} className="block size-full">
      {body}
    </Link>
  );
};

const SkillCards1 = (props: any) => {
  const sectionData = props.data?.data ?? props.data ?? {};
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  // Hover config is desktop-only; also stays off while the breakpoint resolves.
  const isMobile = useIsMobile(1080);

  const brochureUrl = useSelector(
    (state: RootState) => state.globalslice?.course?.brochure || "",
  );

  const {
    heading = "",
    heading_color = "#101112",
    sub_heading = "",
    sub_heading_color = "#101112",
    cta_label = "",
    cta_action = "apply",
    cta_icon = "",
    cta_url = "",
    metadata = [],
    status = true,
  } = sectionData;

  const cards = useMemo(() => {
    const rows: Row[] = Array.isArray(metadata) ? metadata : [];
    return rows
      .filter((r) => isOn(r?.meta_status))
      .filter((r) => r?.meta_image)
      // PRD: at least one of title / subtitle / paragraph per container.
      .filter(
        (r) =>
          String(r?.meta_title ?? "").trim() ||
          String(r?.meta_subtitle ?? "").trim() ||
          String(r?.meta_paragraph ?? "").trim(),
      )
      .sort((a, b) => Number(a?.meta_priority ?? 0) - Number(b?.meta_priority ?? 0));
  }, [metadata]);

  if (status === false || status === 0 || status === "0") return null;
  if (!String(heading ?? "").trim() || cards.length === 0) return null;

  const needsToggle = cards.length > MOBILE_VISIBLE; // narrow view always first
  const desktopNeedsToggle = cards.length > DESKTOP_VISIBLE;

  const handleCtaClick = () => {
    const link = String(cta_url || "").trim();
    if (link) {
      if (link.startsWith("#")) {
        document.querySelector(link)?.scrollIntoView({ behavior: "smooth" });
      } else {
        window.open(link, /^https?:\/\//i.test(link) ? "_blank" : "_self");
      }
      return;
    }
    dispatch(
      handleOpenLeaddrop(
        String(cta_action).toLowerCase() === "download"
          ? { variant: LEAD_DROP_VARIANTS.DOWNLOAD, downloadLink: brochureUrl }
          : { variant: LEAD_DROP_VARIANTS.REGISTER },
      ),
    );
  };

  return (
    // The section is the size container; all @min/@max classes below respond to
    // ITS width. Layout styles live on the inner div — an element cannot be
    // sized by a query on itself.
    <section className="w-full bg-white @container">
      <div
        className={cn(
          "flex w-full flex-col items-center",
          "gap-[24px] px-[24px] py-[40px]",
          "@min-[1080px]:gap-[28px] @min-[1080px]:px-[40px] @min-[1080px]:py-[30px]",
          "@min-[1230px]:gap-[32px] @min-[1230px]:px-[60px] @min-[1230px]:py-[40px]",
          "@min-[1430px]:gap-[40px] @min-[1430px]:px-[80px] @min-[1430px]:py-[60px]",
          "@min-[1750px]:gap-[60px] @min-[1750px]:px-[160px]",
        )}
      >
        {/* Heading + sub heading */}
        <div
          className={cn(
            "flex w-full flex-col items-center text-center [word-break:break-word]",
            "gap-[22px] @min-[1080px]:gap-[14px] @min-[1230px]:gap-[17px] @min-[1430px]:gap-[22px]",
          )}
        >
          <h2
            className={cn(
              "w-full font-poppins font-semibold leading-[1.25] @min-[1750px]:leading-[1.2]",
              "text-[28px] tracking-[-0.42px]",
              "@min-[1230px]:text-[32px] @min-[1230px]:tracking-[-0.48px]",
              "@min-[1430px]:text-[36px] @min-[1430px]:tracking-[-0.54px]",
              "@min-[1750px]:text-[42px] @min-[1750px]:tracking-[-0.63px]",
            )}
            style={{ color: heading_color || "#101112" }}
          >
            {heading}
          </h2>
          {String(sub_heading ?? "").trim() && (
            <p
              className={cn(
                "w-full font-poppins leading-[1.65]",
                "text-[14px] font-medium",
                "@min-[1080px]:text-[16px] @min-[1080px]:font-normal @min-[1080px]:leading-[1.6]",
                "@min-[1230px]:leading-[1.65] @min-[1430px]:text-[17px] @min-[1750px]:text-[18px]",
              )}
              style={{ color: sub_heading_color || "#101112" }}
            >
              {sub_heading}
            </p>
          )}
        </div>

        {/* Containers: 1 col stacked when narrow, 2 per row from 1080 up. When
            the number of VISIBLE cards is odd, the last one centres in its row
            (visible count depends on the View More state: collapsed shows at
            most 4, expanded shows all). */}
        <div
          className={cn(
            "grid w-full grid-cols-1 gap-[24px]",
            // Narrow view: equal row tracks so every stacked card matches the
            // tallest one (cards fill their track via size-full; the text
            // column is flex-1, so the image stays top and text absorbs the
            // extra height). Wide view keeps natural per-row heights.
            "@max-[1080px]:auto-rows-fr",
            "@min-[1080px]:grid-cols-2 @min-[1080px]:gap-x-[20px] @min-[1080px]:gap-y-[28px]",
            "@min-[1230px]:gap-x-[32px] @min-[1230px]:gap-y-[32px]",
            "@min-[1430px]:gap-x-[40px] @min-[1430px]:gap-y-[40px]",
            "@min-[1750px]:gap-x-[50px] @min-[1750px]:gap-y-[50px]",
          )}
        >
          {cards.map((row, i) => {
            const visibleWide = expanded
              ? cards.length
              : Math.min(cards.length, DESKTOP_VISIBLE);
            const centreLast =
              visibleWide % 2 === 1 && i === visibleWide - 1;
            return (
              <div
                key={row.id ?? i}
                className={cn(
                  // Collapsed: 3 visible when narrow, 4 when wide (PRD) — pure
                  // CSS so the split holds on the first server render.
                  !expanded && i >= DESKTOP_VISIBLE && "hidden",
                  !expanded && i === MOBILE_VISIBLE && "@max-[1080px]:hidden",
                  // Odd visible count: the last card spans the final row and
                  // centres at a normal cell width instead of sitting left.
                  centreLast &&
                    "@min-[1080px]:col-span-full @min-[1080px]:justify-self-center @min-[1080px]:w-[calc(50%-10px)]",
                )}
              >
                <SkillCard row={row} hoverEnabled={isMobile === false} />
              </div>
            );
          })}
        </div>

        {/* View More opens ALL remaining at once; View Less collapses (PRD). */}
        {needsToggle && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-cta-name={expanded ? "view_less" : "view_more"}
            className={cn(
              "cursor-pointer font-poppins text-black underline underline-offset-2",
              "text-[16px] font-semibold",
              "@min-[1080px]:text-[14px] @min-[1080px]:font-medium @min-[1080px]:tracking-[0.28px]",
              "@min-[1230px]:font-semibold @min-[1230px]:tracking-normal @min-[1430px]:text-[16px]",
              // 4 cards: only the narrow view hides one, wide needs no toggle.
              !desktopNeedsToggle && "@min-[1080px]:hidden",
            )}
          >
            {expanded ? "View Less" : "View More"}
          </button>
        )}

        {/* Optional closing CTA — system behaviour (lead drawer / URL override). */}
        {String(cta_label ?? "").trim() && (
          <button
            type="button"
            onClick={handleCtaClick}
            data-cta-name="apply_now"
            className={cn(
              "flex w-full cursor-pointer items-center justify-center gap-[10px] rounded-[8px] @min-[1080px]:w-auto",
              "bg-[#9F1C34] px-[24px] py-[16px] text-white",
              "drop-shadow-[0px_10px_10px_rgba(202,125,139,0.6)] transition-opacity hover:opacity-90",
              "font-poppins font-medium leading-[1.4] text-[16px] @min-[1080px]:text-[14px] @min-[1430px]:text-[16px]",
            )}
          >
            <span className="whitespace-nowrap">{cta_label}</span>
            {/* Admin-uploaded icon wins; empty = the built-in export arrow. */}
            {cta_icon ? (
              <span className="relative size-[24px] shrink-0 @min-[1080px]:size-[20px] @min-[1230px]:size-[24px]">
                <Image src={cta_icon} alt="" fill sizes="24px" className="object-contain" />
              </span>
            ) : (
              <ExportIcon className="size-[24px] shrink-0 @min-[1080px]:size-[20px] @min-[1230px]:size-[24px]" />
            )}
          </button>
        )}
      </div>
    </section>
  );
};

export default SkillCards1;
